import { buildInitMessage, isPopupMessage } from '@cesto/web-protocol';
import { buildFallbackPanel } from './dialog-fallback-panel';
import { ensureDialogStyles } from './dialog-styles';
import { buildThemeQuery } from './theme-params';
import type {
  InvestErrorEvent,
  InvestOptions,
  InvestHandoffEvent,
  InvestPositionClosedEvent,
  InvestScheduledEvent,
  InvestSession,
  InvestSuccessEvent,
  InvestVerifiedEvent,
} from './types';
import { handleWalletRequest } from './wallet-request';

/**
 * Once the iframe handshake has failed on this tab, subsequent invest()
 * calls skip the iframe and go straight to popup mode.
 */
export const FORCE_POPUP_STORAGE_KEY = 'cesto-invest:force-popup';

// Generous on purpose: a cold server (dev compile, cold lambda) can take
// several seconds before the invest app can post `ready`, and a false
// timeout writes the force-popup memory below.
// Exported so the tests drive the fallback path off THIS value rather than a
// hardcoded copy — they previously hardcoded 10_000, so raising this to 20_000
// left every fallback-panel test silently timing out before the panel existed.
export const READY_TIMEOUT_MS = 20_000;
// A failed handshake routes this tab to popup mode for a while, not forever:
// transient causes (server restart mid-click) should not permanently
// downgrade the experience. A later SUCCESSFUL handshake clears it early.
const FORCE_POPUP_TTL_MS = 10 * 60_000;

/** True when a dialog handshake failed on this tab recently. */
export function isPopupForced(): boolean {
  try {
    const stamp = window.sessionStorage.getItem(FORCE_POPUP_STORAGE_KEY);
    if (!stamp) return false;
    const at = Number.parseInt(stamp, 10);
    // Guards a non-numeric value only. The legacy no-timestamp format was the
    // literal '1', which parses to a FINITE 1 and so falls through to the TTL
    // compare below — where `Date.now() - 1` is astronomically larger than the
    // TTL, and it reads as expired. That is the correct outcome; it just does
    // not happen here, which the previous comment claimed.
    if (!Number.isFinite(at)) return false;
    return Date.now() - at < FORCE_POPUP_TTL_MS;
  } catch {
    return false;
  }
}

function rememberPopupForced(): void {
  try {
    window.sessionStorage.setItem(FORCE_POPUP_STORAGE_KEY, String(Date.now()));
  } catch {
    // Storage unavailable (privacy mode): the fallback still works, it is
    // just not remembered across invest() calls.
  }
}

/** Called on a successful handshake: the iframe path evidently works here. */
export function clearPopupForced(): void {
  try {
    window.sessionStorage.removeItem(FORCE_POPUP_STORAGE_KEY);
  } catch {
    // Nothing stored.
  }
}

interface DialogControllerConfig {
  baseUrl: string;
  baseOrigin: string;
  apiKey: string;
  options: InvestOptions;
  /**
   * Opens the popup path with the same options. Only ever invoked
   * synchronously from the fallback panel's button click (a fresh user
   * gesture), since window.open outside a gesture would be blocked.
   */
  openPopup: () => void;
}

/**
 * Lifecycle of one in-page invest dialog. The invest app renders its own
 * dialog chrome (backdrop, modal / bottom sheet, close button) over a
 * transparent page, so the SDK's side is just a full-viewport transparent
 * iframe plus: body scroll-lock, ESC-on-partner-page dismissal, the same
 * pinned message bridge as popup mode, and the handshake-timeout fallback
 * to popup mode. Backdrop clicks land inside the iframe; the app's overlay
 * handles them and posts 'close'.
 *
 * The iframe carries no `sandbox` attribute on purpose: it frames our own
 * trusted origin and OAuth popups opened from inside it must work.
 */
export class DialogController {
  readonly session: InvestSession;

  private readonly baseOrigin: string;
  private readonly apiKey: string;
  private readonly options: InvestOptions;
  private readonly openPopup: () => void;

  private iframe: HTMLIFrameElement | null;
  private fallbackOverlay: HTMLDivElement | null = null;
  private readonly previouslyFocused: Element | null;
  private readonly previousBodyOverflow: string;
  private readonly firedCallbacks = new Set<string>();
  private readyTimer: ReturnType<typeof setTimeout> | null;
  private finalized = false;

  constructor(config: DialogControllerConfig) {
    this.baseOrigin = config.baseOrigin;
    this.apiKey = config.apiKey;
    this.options = config.options;
    this.openPopup = config.openPopup;

    ensureDialogStyles();

    const themeQuery = buildThemeQuery(config.options.theme);
    const url =
      `${config.baseUrl}/invest/${encodeURIComponent(config.options.basket)}` +
      `?pk=${encodeURIComponent(config.apiKey)}` +
      `&mode=dialog&parent=${encodeURIComponent(window.location.origin)}` +
      `${themeQuery}`;

    const iframe = document.createElement('iframe');
    iframe.className = 'cesto-invest-frame';
    iframe.allow = 'clipboard-write';
    iframe.title = 'Cesto invest';
    iframe.src = url;
    iframe.style.position = 'fixed';
    iframe.style.inset = '0';
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.border = '0';
    iframe.style.background = 'transparent';
    iframe.style.zIndex = '2147483000';
    // Without this, a dark-scheme embedder makes the UA paint an opaque
    // canvas behind the frame, defeating the transparency.
    iframe.style.colorScheme = 'normal';
    // Invisible until 'ready' arrives: the app paints its own dialog
    // entrance, this only avoids a flash of half-loaded page.
    iframe.style.opacity = '0';
    // ...and INERT until then, too. The frame is full-viewport and fixed, so
    // while it is merely transparent it is still an invisible wall swallowing
    // every click on the partner's page — for up to READY_TIMEOUT_MS (20s) on
    // a cold start, which reads to the visitor as the site having frozen.
    // Cleared alongside the opacity in the 'ready' branch below; the fallback
    // panel (which replaces the frame outright) is unaffected.
    iframe.style.pointerEvents = 'none';
    this.iframe = iframe;

    this.previouslyFocused = document.activeElement;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    window.addEventListener('message', this.handleMessage);
    document.addEventListener('keydown', this.handleKeydown);

    document.body.appendChild(iframe);
    iframe.focus();

    // If 'ready' never arrives (framing blocked, extension interference),
    // swap to the SDK-rendered fallback panel.
    this.readyTimer = setTimeout(
      () => this.showFallbackPanel(),
      READY_TIMEOUT_MS,
    );

    const self = this;
    this.session = {
      focus: () => this.focus(),
      close: () => this.dismiss(),
      get isOpen() {
        return self.isOpen;
      },
    };
  }

  get isOpen(): boolean {
    return !this.finalized;
  }

  focus(): void {
    this.iframe?.focus();
  }

  /** User-initiated close (ESC, session.close, frame 'close'). */
  dismiss(): void {
    if (this.finalized) return;
    this.finalize(true);
    this.removeUi();
  }

  private removeUi(): void {
    this.iframe?.remove();
    this.iframe = null;
    this.fallbackOverlay?.remove();
    this.fallbackOverlay = null;
  }

  private readonly handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') this.dismiss();
  };

  private readonly handleMessage = (event: MessageEvent): void => {
    // Same strict pinning as popup mode: only the window of the iframe we
    // injected, only the Cesto origin, only well-formed protocol messages.
    const frameWindow = this.iframe?.contentWindow;
    if (!frameWindow || event.source !== frameWindow) return;
    if (event.origin !== this.baseOrigin) return;
    if (!isPopupMessage(event.data)) return;

    const { type, payload } = event.data;
    switch (type) {
      case 'ready':
        if (this.readyTimer !== null) {
          clearTimeout(this.readyTimer);
          this.readyTimer = null;
        }
        // The iframe path evidently works on this tab: forget any earlier
        // handshake failure so future clicks stay in dialog mode.
        clearPopupForced();
        // Fade the frame in (a transition in the stylesheet, gated on
        // prefers-reduced-motion) and let it take clicks again: the app is
        // ready to paint its dialog.
        if (this.iframe) {
          this.iframe.style.opacity = '1';
          this.iframe.style.pointerEvents = '';
        }
        this.sendInit(frameWindow);
        this.options.onReady?.();
        break;
      case 'context:verified':
        this.options.onVerified?.(payload as unknown as InvestVerifiedEvent);
        break;
      case 'invest:success':
        this.fireOnce('success', () =>
          this.options.onSuccess?.(payload as unknown as InvestSuccessEvent),
        );
        break;
      case 'invest:scheduled':
        this.fireOnce('scheduled', () =>
          this.options.onScheduled?.(
            payload as unknown as InvestScheduledEvent,
          ),
        );
        break;
      case 'invest:failed':
        this.fireOnce('error', () =>
          this.options.onError?.(payload as unknown as InvestErrorEvent),
        );
        break;
      case 'position:closed':
        // Deliberately NOT fireOnce: a user can close more than one position
        // without leaving the dialog, and each closure is a real event the
        // partner needs. Unrelated to `dialog:closed` below.
        this.options.onPositionClosed?.(
          payload as unknown as InvestPositionClosedEvent,
        );
        break;
      case 'auth:handoff':
        // The dialog opened a top-level Cesto window because the chosen
        // sign-in refuses to be framed. NOT an ending: this controller keeps
        // listening, and the same dialog still reports the invest.
        this.options.onHandoff?.(payload as unknown as InvestHandoffEvent);
        break;
      case 'dialog:closed':
        this.dismiss();
        break;
      case 'wallet:detect':
      case 'wallet:connect':
      case 'wallet:sign-message':
      case 'wallet:sign-transaction':
        // Wallet relay: the extension providers only exist here on the
        // partner page, so the dialog proxies detect/connect/sign through
        // the SDK. Fire-and-forget: each request is answered with exactly
        // one response (or wallet:error) posted back to the pinned frame
        // window, and never touches the once-only invest callbacks or the
        // dismissal lifecycle.
        //
        // A wallet round-trip is a human deciding in an extension popup, so it
        // can outlive the dialog: the user can dismiss (or ESC) while the
        // prompt is still up. Posting into a torn-down frame throws, and
        // because nothing awaits this call that surfaces as an unhandled
        // rejection on the partner's page. Drop the reply instead — the
        // requester no longer exists (LOW-4).
        void handleWalletRequest(type, payload, (message) => {
          if (this.finalized || this.iframe?.contentWindow !== frameWindow) {
            return;
          }
          frameWindow.postMessage(message, this.baseOrigin);
        });
        break;
      default:
        // Future informational events: ignored, never errored.
        break;
    }
  };

  private sendInit(frameWindow: Window): void {
    const { basket, amountUsd } = this.options;
    // Explicit targetOrigin, never '*': init carries the publishable key and
    // must only ever reach the Cesto origin we framed.
    frameWindow.postMessage(
      buildInitMessage({
        // Wire field — named by the protocol, not by our config.
        publishableKey: this.apiKey,
        basket,
        ...(amountUsd !== undefined ? { prefill: { amountUsd } } : {}),
      }),
      this.baseOrigin,
    );
  }

  private showFallbackPanel(): void {
    if (this.finalized) return;
    this.readyTimer = null;
    // Remember the failure for this tab session so the next invest() goes
    // straight to popup mode instead of retrying the iframe.
    rememberPopupForced();
    this.iframe?.remove();
    this.iframe = null;

    // The SDK no longer paints a backdrop of its own (the app's chrome
    // lives inside the iframe), so the fallback panel brings its own dim
    // overlay.
    const overlay = document.createElement('div');
    overlay.className = 'cesto-invest-fallback-overlay';
    overlay.appendChild(
      buildFallbackPanel({
        onContinue: () => {
          // Continuing in a popup, not abandoning: skip onClose.
          this.finalize(false);
          this.removeUi();
          this.openPopup();
        },
        onDismiss: () => this.dismiss(),
      }),
    );
    this.fallbackOverlay = overlay;
    document.body.appendChild(overlay);
  }

  private fireOnce(key: string, fire: () => void): void {
    if (this.firedCallbacks.has(key)) return;
    this.firedCallbacks.add(key);
    fire();
  }

  private finalize(fireClose: boolean): void {
    if (this.finalized) return;
    this.finalized = true;
    if (this.readyTimer !== null) {
      clearTimeout(this.readyTimer);
      this.readyTimer = null;
    }
    window.removeEventListener('message', this.handleMessage);
    document.removeEventListener('keydown', this.handleKeydown);
    document.body.style.overflow = this.previousBodyOverflow;
    if (this.previouslyFocused instanceof HTMLElement) {
      this.previouslyFocused.focus();
    }
    if (fireClose) this.fireOnce('close', () => this.options.onClose?.());
  }
}
